import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { topicsService } from '@/services/topics';
import { RootState } from '@store/store';
import { activeWorkspaceSelector } from '@store/features/active-workspace/selectors'
import { tamedDatasService } from '@/services/tameddatas';
import { TopicFolder } from '@/types/topic';

// Track in-flight requests to prevent duplicate calls (handles React StrictMode double-invocation)
const inFlightRequests: { [key: string]: Promise<any> } = {};

export const fetchTopics = createAsyncThunk(
    '/fetchTopics',
    async (payload, { dispatch, getState }) => {
        const state = getState() as RootState
        const workspaceId = state.activeWorkspace?.id;
        if (!workspaceId || workspaceId === 'undefined' || workspaceId === 'null' || workspaceId.trim() === '') {
            console.warn('fetchTopics called without valid active workspace:', workspaceId);
            return [];
        }

        // Check if there's already an in-flight request for this workspace
        const requestKey = `topics_${workspaceId}`;
        if (inFlightRequests[requestKey]) {
            return inFlightRequests[requestKey];
        }

        // Create and store the promise
        const requestPromise = topicsService.fetchTopics(workspaceId)
            .finally(() => {
                delete inFlightRequests[requestKey];
            });

        inFlightRequests[requestKey] = requestPromise;
        return requestPromise;
    }
)

export const fetchTamedData = createAsyncThunk(
    '/fetchTamedData',
    async (payload: { topicId: string }) => {
        const { topicId } = payload;
        if (!topicId || topicId === 'undefined' || topicId === 'null') {
            console.warn('fetchTamedData called with invalid topicId:', topicId);
            return [];
        }

        // Check if there's already an in-flight request for this topic
        const requestKey = `tamedData_${topicId}`;
        if (inFlightRequests[requestKey]) {
            // Return the existing promise to avoid duplicate API calls
            return inFlightRequests[requestKey];
        }

        // Create and store the promise
        const requestPromise = tamedDatasService.fetchTamedDatas(topicId)
            .finally(() => {
                // Clean up after request completes
                delete inFlightRequests[requestKey];
            });

        inFlightRequests[requestKey] = requestPromise;
        return requestPromise;
    }
)

export const updateTopic = createAsyncThunk(
    '/updateTopic',
    async (payload: { id: string; name: string; description: string }) => {
        const { id, name, description } = payload;
        if (!id) {
            throw new Error('Topic ID is required');
        }
        const response = await topicsService.updateTopic(id, name, description);
        return response;
    }
)

// Topic Folder Async Thunks
export const fetchTopicFolders = createAsyncThunk(
    'topics/fetchTopicFolders',
    async (payload: { workspaceId: string }) => {
        const { workspaceId } = payload;
        if (!workspaceId || workspaceId === 'undefined') {
            console.warn('fetchTopicFolders called with invalid workspaceId:', workspaceId);
            return [];
        }

        const requestKey = `topicFolders_${workspaceId}`;
        if (inFlightRequests[requestKey]) {
            return inFlightRequests[requestKey];
        }

        const requestPromise = topicsService.fetchTopicFolders(workspaceId)
            .finally(() => {
                delete inFlightRequests[requestKey];
            });

        inFlightRequests[requestKey] = requestPromise;
        return requestPromise;
    }
)

export const createTopicFolder = createAsyncThunk(
    'topics/createTopicFolder',
    async (payload: { name: string; workspace_id: string; parent_id?: string | null }) => {
        return await topicsService.createTopicFolder(payload);
    }
)

export const updateTopicFolder = createAsyncThunk(
    'topics/updateTopicFolder',
    async (payload: { id: string; data: { name?: string; parent_id?: string | null } }) => {
        const { id, data } = payload;
        return await topicsService.updateTopicFolder(id, data);
    }
)

export const deleteTopicFolder = createAsyncThunk(
    'topics/deleteTopicFolder',
    async (payload: { id: string }) => {
        const { id } = payload;
        await topicsService.deleteTopicFolder(id);
        return id;
    }
)

export const moveTopicToFolder = createAsyncThunk(
    'topics/moveTopicToFolder',
    async (payload: { topicId: string; folderId: string | null }) => {
        const { topicId, folderId } = payload;
        await topicsService.moveTopicToFolder(topicId, folderId);
        return { topicId, folderId };
    }
)

// Helper function to get initial state from localStorage
const getInitialActiveTopic = (): string | null => {
    if (typeof window !== 'undefined') {
        const saved = localStorage.getItem('activeTopic');
        if (saved && saved !== 'undefined' && saved !== 'null' && saved.trim() !== '') {
            return saved;
        }
    }
    return null;
};

const topicsSlice = createSlice({
    name: 'topics',
    initialState: {
        topics: [] as any[],
        activeTopic: getInitialActiveTopic(),
        currentTamedDatas: [] as any,
        currentTamedData: [] as any, // array of target tameddatas
        currentMode: 'relevance', // mode = relevance || loop
        // Topic folders state
        topicFolders: [] as TopicFolder[],
        expandedTopicFolders: [] as string[],
        currentTopicFolderId: null as string | null,
        status: 'idle',
        error: null as string | null
    },
    reducers: {
        setTopics: (state, action: PayloadAction<any>) => {
            state.topics = action.payload
        },
        setActiveTopic: (state, action: PayloadAction<any>) => {
            const topicId = action.payload;
            state.activeTopic = topicId;
            
            // Persist to localStorage
            if (typeof window !== 'undefined') {
                if (topicId && topicId !== 'undefined' && topicId !== 'null' && topicId.trim() !== '') {
                    localStorage.setItem('activeTopic', topicId);
                } else {
                    localStorage.removeItem('activeTopic');
                }
            }
        },
        setTopicTamedDatasSelected: (state, action: PayloadAction<any>) => {
            state.currentTamedData = action.payload
        },
        addTopicTamedDatas: (state, action: PayloadAction<any>) => {
            state.currentTamedDatas.push(action.payload)
        },
        removeCurrentTamedDatas: (state, action: PayloadAction<any>) => {
            state.currentTamedDatas = state.currentTamedDatas.filter((tamedData: any) => tamedData.id !== action.payload)
        },
        removeTamedDatasByIds: (state, action: PayloadAction<string[]>) => {
            const idsToRemove = action.payload;
            state.currentTamedDatas = state.currentTamedDatas.filter((tamedData: any) => !idsToRemove.includes(tamedData.id));
        },
        addTamedDataToList: (state, action: PayloadAction<any>) => {
            // Only add if it doesn't already exist and matches the active topic
            const exists = state.currentTamedDatas.some((td: any) => td.id === action.payload.id);
            if (!exists && action.payload.fk_topics_id === state.activeTopic) {
                state.currentTamedDatas.push(action.payload);
            }
        },
        updateTamedDataInList: (state, action: PayloadAction<{ id: string; name?: string; description?: string; fk_folder_id?: string | null }>) => {
            const { id, ...updates } = action.payload;
            const index = state.currentTamedDatas.findIndex((td: any) => td.id === id);
            if (index !== -1) {
                state.currentTamedDatas[index] = { ...state.currentTamedDatas[index], ...updates };
            }
        },
        setCurrentMode: (state, action: PayloadAction<any>) => {
            state.currentMode = action.payload;
        },
        updateTamedData: (state, action: PayloadAction<{ id: string; name?: string; description?: string }>) => {
            const { id, ...updates } = action.payload;
            const index = state.currentTamedDatas.findIndex((td: any) => td.id === id);
            if (index !== -1) {
                state.currentTamedDatas[index] = { ...state.currentTamedDatas[index], ...updates };
            }
        },
        updateTamedDataByDatasourceId: (state, action: PayloadAction<{ datasourceId: string; updates: any }>) => {
            // Update tamed_data entries linked to a specific datasource
            const { datasourceId, updates } = action.payload;
            state.currentTamedDatas = state.currentTamedDatas.map((tamedData: any) => {
                return tamedData?.fk_datasources_id === datasourceId
                    ? { ...tamedData, ...updates }
                    : tamedData;
            });
        },
        // Topic operations from WebSocket broadcasts
        addTopic: (state, action: PayloadAction<any>) => {
            // Only add if topic doesn't already exist
            const exists = (state.topics as any[]).some((t: any) => t.id === action.payload.id);
            if (!exists) {
                (state.topics as any[]).push(action.payload);
            }
        },
        updateTopicInList: (state, action: PayloadAction<{ id: string; name?: string; description?: string }>) => {
            const { id, ...updates } = action.payload;
            const index = (state.topics as any[]).findIndex((t: any) => t.id === id);
            if (index !== -1) {
                (state.topics as any[])[index] = { ...(state.topics as any[])[index], ...updates };
            }
        },
        removeTopic: (state, action: PayloadAction<string>) => {
            state.topics = (state.topics as any[]).filter((t: any) => t.id !== action.payload);
            // If the deleted topic was active, clear it
            if (state.activeTopic === action.payload) {
                state.activeTopic = null;
                if (typeof window !== 'undefined') {
                    localStorage.removeItem('activeTopic');
                }
            }
        },
        // Topic folder reducers
        setTopicFolders: (state, action: PayloadAction<TopicFolder[]>) => {
            state.topicFolders = action.payload;
        },
        addTopicFolder: (state, action: PayloadAction<TopicFolder>) => {
            const exists = state.topicFolders.some(f => f.id === action.payload.id);
            if (!exists) {
                state.topicFolders.push(action.payload);
            }
        },
        updateTopicFolderInState: (state, action: PayloadAction<TopicFolder>) => {
            const index = state.topicFolders.findIndex(f => f.id === action.payload.id);
            if (index !== -1) {
                state.topicFolders[index] = { ...state.topicFolders[index], ...action.payload };
            }
        },
        removeTopicFolderFromState: (state, action: PayloadAction<string>) => {
            state.topicFolders = state.topicFolders.filter(f => f.id !== action.payload);
            // Also remove from expanded folders
            state.expandedTopicFolders = state.expandedTopicFolders.filter(id => id !== action.payload);
            // If current folder was deleted, go to root
            if (state.currentTopicFolderId === action.payload) {
                state.currentTopicFolderId = null;
            }
        },
        toggleTopicFolderExpanded: (state, action: PayloadAction<string>) => {
            const folderId = action.payload;
            const index = state.expandedTopicFolders.indexOf(folderId);
            if (index === -1) {
                state.expandedTopicFolders.push(folderId);
            } else {
                state.expandedTopicFolders.splice(index, 1);
            }
        },
        setTopicFolderExpanded: (state, action: PayloadAction<{ folderId: string; expanded: boolean }>) => {
            const { folderId, expanded } = action.payload;
            const index = state.expandedTopicFolders.indexOf(folderId);
            if (expanded && index === -1) {
                state.expandedTopicFolders.push(folderId);
            } else if (!expanded && index !== -1) {
                state.expandedTopicFolders.splice(index, 1);
            }
        },
        setCurrentTopicFolderId: (state, action: PayloadAction<string | null>) => {
            state.currentTopicFolderId = action.payload;
        },
        clearTopicFolders: (state) => {
            state.topicFolders = [];
            state.expandedTopicFolders = [];
            state.currentTopicFolderId = null;
        },
        // Update topic's folder assignment in state
        updateTopicFolderAssignment: (state, action: PayloadAction<{ topicId: string; folderId: string | null }>) => {
            const { topicId, folderId } = action.payload;
            const index = state.topics.findIndex((t: any) => t.id === topicId);
            if (index !== -1) {
                state.topics[index] = { ...state.topics[index], fk_folder_id: folderId };
            }
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchTopics.pending, (state) => {
            state.status = 'loading'
        })
        builder.addCase(fetchTopics.fulfilled, (state, action) => {
            state.status = 'succeeded'
            state.topics = action.payload
        })
        builder.addCase(fetchTopics.rejected, (state, action) => {
            state.status = 'failed'
            state.error = action.error.message || null
        })
        builder.addCase(fetchTamedData.pending, (state) => {
            state.status = 'loading'
        })
        builder.addCase(fetchTamedData.fulfilled, (state, action) => {
            state.status = 'succeeded'
            state.currentTamedDatas = action.payload
        })
        builder.addCase(fetchTamedData.rejected, (state, action) => {
            state.status = 'failed'
            state.error = action.error.message || null
        })
        builder.addCase(updateTopic.pending, (state) => {
            state.status = 'loading'
        })
        builder.addCase(updateTopic.fulfilled, (state, action) => {
            state.status = 'succeeded'
            // Update the topic in the topics array
            const updatedTopic = action.payload;
            const index = (state.topics as any[]).findIndex((t: any) => t.id === updatedTopic.id);
            if (index !== -1) {
                (state.topics as any[])[index] = { ...(state.topics as any[])[index], ...updatedTopic };
            }
        })
        builder.addCase(updateTopic.rejected, (state, action) => {
            state.status = 'failed'
            state.error = action.error.message || null
        })
        // Topic folder extra reducers
        builder.addCase(fetchTopicFolders.fulfilled, (state, action) => {
            state.topicFolders = action.payload;
        })
        builder.addCase(createTopicFolder.fulfilled, (state, action) => {
            state.topicFolders.push(action.payload);
        })
        builder.addCase(updateTopicFolder.fulfilled, (state, action) => {
            const index = state.topicFolders.findIndex(f => f.id === action.payload.id);
            if (index !== -1) {
                state.topicFolders[index] = action.payload;
            }
        })
        builder.addCase(deleteTopicFolder.fulfilled, (state, action) => {
            state.topicFolders = state.topicFolders.filter(f => f.id !== action.payload);
            state.expandedTopicFolders = state.expandedTopicFolders.filter(id => id !== action.payload);
            if (state.currentTopicFolderId === action.payload) {
                state.currentTopicFolderId = null;
            }
        })
        builder.addCase(moveTopicToFolder.fulfilled, (state, action) => {
            const { topicId, folderId } = action.payload;
            const index = state.topics.findIndex((t: any) => t.id === topicId);
            if (index !== -1) {
                state.topics[index] = { ...state.topics[index], fk_folder_id: folderId };
            }
        })
    }
})

export const {
    setTopics,
    addTopicTamedDatas,
    removeCurrentTamedDatas,
    removeTamedDatasByIds,
    addTamedDataToList,
    updateTamedDataInList,
    setCurrentMode,
    setTopicTamedDatasSelected,
    setActiveTopic,
    updateTamedData,
    updateTamedDataByDatasourceId,
    addTopic,
    updateTopicInList,
    removeTopic,
    // Topic folder actions
    setTopicFolders,
    addTopicFolder,
    updateTopicFolderInState,
    removeTopicFolderFromState,
    toggleTopicFolderExpanded,
    setTopicFolderExpanded,
    setCurrentTopicFolderId,
    clearTopicFolders,
    updateTopicFolderAssignment
} = topicsSlice.actions

// Selectors
export const selectTopicFolders = (state: RootState) => state.topics.topicFolders;
export const selectExpandedTopicFolders = (state: RootState) => state.topics.expandedTopicFolders;
export const selectCurrentTopicFolderId = (state: RootState) => state.topics.currentTopicFolderId;

// Build tree structure from flat folders list
export const selectTopicFolderTree = (state: RootState) => {
    const folders = state.topics.topicFolders;
    const folderMap = new Map<string, TopicFolder & { children: TopicFolder[] }>();
    const rootFolders: (TopicFolder & { children: TopicFolder[] })[] = [];

    // First pass: create map of all folders
    folders.forEach(folder => {
        folderMap.set(folder.id, { ...folder, children: [] });
    });

    // Second pass: build tree structure
    folders.forEach(folder => {
        const folderWithChildren = folderMap.get(folder.id)!;
        if (folder.fk_parent_id && folderMap.has(folder.fk_parent_id)) {
            const parent = folderMap.get(folder.fk_parent_id)!;
            parent.children.push(folderWithChildren);
        } else {
            rootFolders.push(folderWithChildren);
        }
    });

    return rootFolders;
};

export default topicsSlice.reducer